import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, User, ShoppingBag, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';
import CartDrawer from '@/components/CartDrawer';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const { getCartCount, setIsCartOpen } = useCart();
  const { currentUser, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);
  const cartCount = getCartCount();

  const handleLogoClick = () => {
    setClickCount((prev) => prev + 1);
    if (clickTimer) clearTimeout(clickTimer);
    const timer = setTimeout(() => setClickCount(0), 1000);
    setClickTimer(timer);
    if (clickCount + 1 === 3) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  const handleCartClick = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setIsCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <CartDrawer />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="hidden md:flex items-center space-x-8 w-32"></div>

            <button className="md:hidden text-black" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={handleLogoClick}>
              <h1 className="text-lg sm:text-xl font-light tracking-[0.2em] text-black">B&C Store</h1>
            </div>

            <div className="flex items-center space-x-4 sm:space-x-5">
              {currentUser ? (
                <div className="relative">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="text-gray-600 hover:text-black transition-colors">
                    <User size={20} strokeWidth={1.5} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Profile</button>
                      <button onClick={() => { navigate('/my-orders'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Orders</button>
                      <button onClick={async () => { await logout(); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 font-light">Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="text-gray-600 hover:text-black transition-colors">
                  <User size={20} strokeWidth={1.5} />
                </button>
              )}
              
              <button onClick={handleCartClick} className="text-gray-600 hover:text-black transition-colors relative">
                <ShoppingBag size={20} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-light">{cartCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">Home</button>
              <button onClick={() => { navigate('/products'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">Products</button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-500 hover:text-black mb-6 sm:mb-8 transition-colors font-light text-sm"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            <span>Back to Home</span>
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6 sm:p-10"
          >
            <h1 className="text-3xl sm:text-4xl font-light tracking-wide mb-8 text-center">Terms and Policies</h1>

            <div className="text-sm text-gray-700 font-light space-y-8">
              <section>
                <h3 className="font-medium text-black text-lg mb-3">1. About Us</h3>
                <p>bcstore.in is an online store offering mobile accessories to customers in India.</p>
                <p className="mt-2">Contact Email: contactus@bcstore.in</p>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">2. Eligibility</h3>
                <p>By using this website, you confirm that:</p>
                <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
                  <li>You are at least 18 years old or accessing under parental supervision.</li>
                  <li>You are legally capable of entering into a binding contract under Indian law.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">3. Products and Services</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>We sell mobile accessories such as chargers, cables, cases, earphones, and related items.</li>
                  <li>Product images are for representation purposes only. Actual products may vary slightly.</li>
                  <li>Availability of products is subject to change without notice.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">4. Pricing and Payment</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>All prices are listed in Indian Rupees (INR).</li>
                  <li>Prices may change without prior notice.</li>
                  <li>Payments must be completed through the payment methods available on the website.</li>
                  <li>We are not responsible for payment failures caused by third-party payment gateways.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">5. Order Confirmation</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Once an order is placed, you will receive an order confirmation.</li>
                  <li>We reserve the right to cancel or refuse any order due to product unavailability, pricing errors, or suspicious activity.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">6. Shipping Policy</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Orders are shipped within 2 days from the order date.</li>
                  <li>Delivery timelines may vary based on location, courier service, and unforeseen circumstances.</li>
                  <li>bcstore.in is not responsible for delays caused by courier partners, natural events, or government restrictions.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">7. Return and Replacement Policy</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>We offer a 7-day return policy from the date of delivery.</li>
                  <li>Products must be unused, undamaged, and returned in original packaging.</li>
                  <li>Certain items may not be eligible for return due to hygiene or usage reasons.</li>
                  <li>Return requests must be initiated by contacting contactus@bcstore.in.</li>
                  <li>Refunds, if applicable, will be processed after inspection of the returned item.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">8. Cancellation Policy</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Orders can be cancelled before shipping.</li>
                  <li>Once shipped, cancellation is not allowed and the return policy will apply.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">9. User Responsibilities</h3>
                <p>You agree not to:</p>
                <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
                  <li>Use the website for illegal or unauthorized purposes.</li>
                  <li>Provide false or misleading information.</li>
                  <li>Attempt to damage, hack, or disrupt the website or its services.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">10. Intellectual Property</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>All content on bcstore.in including logos, images, text, and designs is the property of bcstore.in.</li>
                  <li>You may not copy, reproduce, or distribute any content without written permission.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">11. Limitation of Liability</h3>
                <p>bcstore.in shall not be liable for:</p>
                <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
                  <li>Indirect, incidental, or consequential damages.</li>
                  <li>Loss arising from misuse of products.</li>
                  <li>Website downtime or technical issues beyond our control.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black text-lg mb-3">12. Changes to Terms</h3>
                <p>We reserve the right to update or modify these Terms at any time without prior notice. Continued use of the website constitutes acceptance of the revised Terms.</p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
